import { canAuthorAbilities } from './abilityAuthoringAccess';
/**
 * AbilityBrowser (formerly SpellsInventoryWindow)
 *
 * Displays the abilities the player has trained at their current level.
 * Allows viewing detailed attributes (homing, mana, cooldown) and
 * dragging to the hotbar.
 *
 * Visual modernization pass: restyled onto the shared theme tokens +
 * primitives (Button / Badge) in the same idiom as ShopView / QuestView —
 * replacing the hand-rolled inline-style buttons and tag spans with semantic
 * tokens (COLORS.textMuted / textSecondary / surfaceSunken / overlayScrimLight).
 * The two-pane layout (wide ItemSlot icon grid on the left + a fixed
 * 220px detail sidebar on the right) is preserved, so it is a visual no-op for
 * the structure. Data + actions still flow through the real abilityRegistry /
 * EventBus / ServiceRegistry paths (on-demand, never polled), so behaviour is
 * unchanged.
 *
 * Ability-school accent colours with no semantic token (the projectile green
 * is RAW.green; the VFX violet is kept verbatim) follow the ShopView
 * RARITY_COLOR literal pattern.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { gameLogger } from '../../../../utils/GameLogger';
import { useGame } from '../../contexts/GameContext';
import { abilityRegistry } from '../../../systems/player/abilities/AbilityRegistry';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { EventBus } from '../../../../shared/EventBus';
import { ItemSlot } from '../../components/ItemKit';
import { AbilityEditorTabs } from '../AbilityEditorTabs';
import { Button } from '../../primitives/Button';
import { Badge } from '../../primitives/Badge';
import { isAbilityTrained } from '../../../../shared/progression/abilityCurve';
import {
  COLORS,
  RAW,
  SPACE,
  RADII,
  FONT_SIZE,
  FONT_WEIGHT,
} from '../../theme';

// ── Layout style tokens (theme-driven; the structural two-pane layout is
// unchanged from the prior pass).

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  color: COLORS.textPrimary,
};

const contentLayoutStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
};

const listPanelStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  borderRight: `1px solid ${COLORS.borderSubtle}`,
  overflowY: 'auto',
  padding: SPACE.lg,
};

const detailPanelStyle: React.CSSProperties = {
  width: 220,
  minHeight: 0,
  background: COLORS.surfaceSunken,
  padding: SPACE.xl,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: SPACE.lg,
};

const abilityGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(48px, 1fr))',
  gap: SPACE.lg,
};

const detailHeaderStyle: React.CSSProperties = {
  borderBottom: `1px solid ${COLORS.borderSubtle}`,
  paddingBottom: SPACE.md,
  marginBottom: SPACE.xs,
};

const abilityNameStyle: React.CSSProperties = {
  margin: 0,
  fontSize: FONT_SIZE.xl,
  color: COLORS.textAccent, // accent gold
};

const abilityTagsStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: SPACE.xs,
  marginTop: SPACE.xs,
};

const descriptionStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.md,
  color: COLORS.textSecondary,
  margin: 0,
  lineHeight: 1.4,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.sm,
  fontWeight: FONT_WEIGHT.bold,
  color: COLORS.textMuted,
  textTransform: 'uppercase',
  marginTop: SPACE.md,
};

// Preview button accents — projectile (green, RAW.green) and vfx (violet, no
// semantic token, kept verbatim per the ShopView literal-colour pattern). Both
// ride the shared Button `secondary` variant with a style override so the
// hover/press feel matches the rest of the UI.
const PREVIEW_ACCENT = {
  projectile: RAW.green, // #2ecc71 — exact
  vfx: '#bb6bd9', // violet — no token
} as const;

function previewButtonStyle(accent: string): React.CSSProperties {
  return { flex: 1, color: accent, borderColor: accent, borderRadius: RADII.md };
}

const bypassToggleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.sm,
  fontSize: FONT_SIZE.sm,
  color: COLORS.textMuted,
  cursor: 'pointer',
  padding: `${SPACE.xs}px 0`,
};

const previewStatusStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.xs,
  color: COLORS.textMuted,
  padding: '4px 6px',
  background: COLORS.overlayScrimLight,
  borderRadius: RADII.sm,
  fontFamily: 'monospace',
  wordBreak: 'break-all',
};

/**
 * AbilityBrowserContent — propless body of the Ability Browser (Phase 2 fold).
 *
 * WindowHost mounts this inside the shared ToolWindow chrome (only while the
 * `spells` window is open — opened via windowBridge.toggle('spells')), so it no
 * longer owns its own ToolWindow / useUI flag. Reads useGame().player (live
 * since the keystone) for class + trained-level filtering; cast/preview
 * self-dispatch through ServiceRegistry / EventBus (unchanged).
 */
export const AbilityAuthoringContent: React.FC = () => {
  const { player: gamePlayer } = useGame();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [bypassCooldowns, setBypassCooldowns] = useState(false);
  const [previewStatus, setPreviewStatus] = useState<string | null>(null);

  const player = gamePlayer || { class: 'rogue' };
  const learnedAbilities = gamePlayer?.learnedAbilities ?? [];

  // Player-facing spellbook truth follows the server-persisted trained list.
  // The level curve only says what a trainer may offer; reaching that level
  // does not grant the ability or expose it in the K-key browser.
  const myAbilities = learnedAbilities.flatMap((abilityId) => {
    const ability = abilityRegistry.getMetadata(abilityId);
    return ability ? [ability] : [];
  });

  const selectedAbility = selectedId ? abilityRegistry.getMetadata(selectedId) : null;

  // Cast an ability directly (like clicking it in WoW's spellbook)
  const castAbility = useCallback((abilityId: string) => {
    if (player && !isAbilityTrained(learnedAbilities, abilityId)) {
      gameLogger.info('ui', `[AbilityBrowser] Ability ${abilityId} is not trained`);
      return;
    }
    gameLogger.info('ui', `[AbilityBrowser] Casting ability: ${abilityId}`);
    const registry = ServiceRegistry.getInstance();
    const entitySystem = registry.get('EntitySystem');
    entitySystem?.getPlayerManager()?.performAbility(abilityId);
  }, [player, learnedAbilities]);

  // Fire projectile directly (bypasses mana/GCD/cooldown)
  const fireProjectileOnly = useCallback((abilityId: string) => {
    const registry = ServiceRegistry.getInstance();
    const entitySystem = registry.get('EntitySystem');
    const projectileSystem = entitySystem?.getProjectileSystem();
    const player = entitySystem?.getPlayerManager()?.getPlayer();
    const meta = abilityRegistry.getMetadata(abilityId);

    if (!projectileSystem || !player || !meta?.projectile) {
      setPreviewStatus(`Cannot fire: projectileSystem=${!!projectileSystem}, player=${!!player}, projectile=${!!meta?.projectile}`);
      return;
    }

    const p = meta.projectile;
    const facingRight = player.facing === 'right';
    const x = player.position.x + (facingRight ? 40 : -40);
    const y = player.position.y - 5;

    projectileSystem.createProjectile({
      x, y, ownerId: player.id, damage: typeof p.damage === 'number' ? p.damage : 10,
      type: 'basic', direction: facingRight ? 'right' : 'left',
      shape: p.shape ?? 'rect', color: p.color ?? 0xFFFFFF,
      speed: p.speed ?? 800, width: p.width ?? 35, height: p.height ?? 3,
      rotationSpeed: p.rotationSpeed ?? 0, gravity: p.gravity ?? 0,
      startTime: Date.now(), lifespan: p.lifespan ?? 3000,
      homingStrength: p.homing?.strength ?? 0,
      abilityId,
    });

    setPreviewStatus(`Projectile: ${p.shape} #${(p.color ?? 0).toString(16)} at (${x.toFixed(0)},${y.toFixed(0)}) spd:${p.speed}`);
  }, []);

  // Show VFX only
  const showVfxOnly = useCallback((abilityId: string) => {
    const registry = ServiceRegistry.getInstance();
    const entitySystem = registry.get('EntitySystem');
    const player = entitySystem?.getPlayerManager()?.getPlayer();
    const meta = abilityRegistry.getMetadata(abilityId);

    if (!player) {
      setPreviewStatus('Cannot show VFX: no player');
      return;
    }

    const effectId = meta?.vfx?.muzzleFlash || meta?.vfx?.windUp || meta?.vfx?.cast || meta?.vfx?.aura;
    if (!effectId) {
      setPreviewStatus(`No VFX configured for ${abilityId}`);
      return;
    }

    EventBus.getInstance().emit('vfx:spawn', {
      effectId,
      x: player.position.x,
      y: player.position.y,
      targetId: player.id,
    });

    setPreviewStatus(`VFX: ${effectId} at (${player.position.x.toFixed(0)},${player.position.y.toFixed(0)})`);
  }, []);

  // Toggle bypass — temporarily zero out mana/cooldown
  const handleBypassToggle = useCallback((checked: boolean) => {
    setBypassCooldowns(checked);
    if (!selectedId) return;
    const meta = abilityRegistry.getMetadata(selectedId);
    if (!meta) return;

    if (checked) {
      // Store originals and zero out
      (meta as any)._origManaCost = meta.manaCost;
      (meta as any)._origCooldown = meta.cooldown;
      abilityRegistry.updateMetadata(selectedId, { manaCost: 0, cooldown: 0 });
      setPreviewStatus('Bypass ON: manaCost=0, cooldown=0');
    } else {
      // Restore originals
      abilityRegistry.updateMetadata(selectedId, {
        manaCost: (meta as any)._origManaCost ?? meta.manaCost,
        cooldown: (meta as any)._origCooldown ?? meta.cooldown,
      });
      setPreviewStatus('Bypass OFF: original values restored');
    }
  }, [selectedId]);

  const handleDragStart = (id: string, e: React.DragEvent) => {
    gameLogger.debug('ui', '[AbilityBrowser] Drag start:', id);
    if (e.dataTransfer) {
      e.dataTransfer.setData('text/plain', `ability:${id}`);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.dropEffect = 'move';
    }
  };

  useEffect(() => () => {
    if (!bypassCooldowns || !selectedId) return;
    const meta = abilityRegistry.getMetadata(selectedId) as any;
    if (meta) abilityRegistry.updateMetadata(selectedId, { manaCost: meta._origManaCost ?? meta.manaCost, cooldown: meta._origCooldown ?? meta.cooldown });
  }, [bypassCooldowns, selectedId]);
  if (!canAuthorAbilities(typeof window === 'undefined' ? '' : window.location.hostname, gamePlayer?.gmLevel)) return <p>Ability authoring is available to authorized operators.</p>;
  return (
    <div style={containerStyle}>
      <div style={contentLayoutStyle}>
        <div style={listPanelStyle}>
          <div style={{ ...sectionTitleStyle, marginBottom: SPACE.lg }}>Your Abilities</div>
          <div style={abilityGridStyle}>
            {myAbilities.map(ability => (
              <ItemSlot
                key={ability.id}
                item={{
                  id: ability.id,
                  name: ability.name || ability.id,
                  icon: ability.icon,
                }}
                size="md"
                ariaLabel={`${ability.name || ability.id} ability`}
                selected={selectedId === ability.id}
                onClick={() => {
                  gameLogger.debug('ui', '[AbilityBrowser] Selecting ability:', ability.id);
                  setBypassCooldowns(false);
                  setSelectedId(ability.id);
                }}
                onDoubleClick={() => castAbility(ability.id)}
                className="ability-slot"
                draggable={true}
                onDragStart={(e) => handleDragStart(ability.id, e)}
              />
            ))}
          </div>

          <div style={{ marginTop: SPACE.xxl, fontSize: FONT_SIZE.sm, color: COLORS.textMuted }}>
              ℹ️ Drag to hotbar · Double-click to cast
          </div>
        </div>

        <div style={detailPanelStyle}>
          {selectedAbility ? (
            <>
              <div style={detailHeaderStyle}>
                <h3 style={abilityNameStyle}>{selectedAbility.name || selectedAbility.id}</h3>
                <div style={abilityTagsStyle}>
                  {selectedAbility.tags?.map(tag => (
                    <Badge key={tag} tone="neutral">{tag}</Badge>
                  ))}
                </div>
              </div>

              <p style={descriptionStyle}>{selectedAbility.description || 'No description available.'}</p>

              <Button variant="primary" size="sm" fullWidth onClick={() => castAbility(selectedAbility.id)}>
                  Cast {selectedAbility.name || selectedAbility.id}
              </Button>

              {/* Preview Controls */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.xs }}>
                <div style={sectionTitleStyle}>Preview</div>
                <div style={{ display: 'flex', gap: SPACE.xs }}>
                  <Button variant="secondary" size="sm" style={previewButtonStyle(PREVIEW_ACCENT.projectile)}
                    onClick={() => fireProjectileOnly(selectedAbility.id)}>
                      Projectile
                  </Button>
                  <Button variant="secondary" size="sm" style={previewButtonStyle(PREVIEW_ACCENT.vfx)}
                    onClick={() => showVfxOnly(selectedAbility.id)}>
                      VFX
                  </Button>
                </div>
                <label style={bypassToggleStyle}>
                  <input type="checkbox" checked={bypassCooldowns} title="Bypass mana and cooldown checks"
                    style={{ cursor: 'pointer' }}
                    onChange={e => handleBypassToggle(e.target.checked)} />
                    Bypass Mana + Cooldown
                </label>
                {previewStatus && <div style={previewStatusStyle}>{previewStatus}</div>}
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, marginTop: SPACE.lg }}>
                <AbilityEditorTabs ability={selectedAbility} onCastAbility={castAbility} />
              </div>
            </>
          ) : (
            <div style={{ color: COLORS.textMuted, fontSize: FONT_SIZE.md, textAlign: 'center', marginTop: 40 }}>
                Select an ability to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
